// Util imports
import React, { useState, useEffect, useCallback } from "react";
import { SERVER_ERROR } from "src/utils/errors";
import {
  FEEDBACK_DIGITAL_DATA,
  OMNITURE_TRACKING_EVENTS,
  setSatelliteDigitalData,
} from "src/utils/omniture";

// Hook imports
import useLocalStorage from "src/components/hooks/useLocalStorage";
import useMergeState from "src/components/hooks/useMergeState";

// Style imports
import styles from "src/styles/components/elements/feedback.module.scss";

const SingleFeedback = React.memo(({ contentId, src, onFeedback, feedback, onSave, isActive, url }) => {
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  useEffect(() => {
    setFeedbackGiven(false);
  }, [contentId]);

  const giveFeedback = async () => {
    onFeedback({ isFeedbackGiven: true, error: null });

    try {
      const res = await url({ faqId: contentId, feedback });
      if (res?.data?._id) {
        onSave(feedback);
        setFeedbackGiven(true);

        // Omniture tracking
        const omniDigitalData = {
          pageName: FEEDBACK_DIGITAL_DATA.pageName,
          smiley: FEEDBACK_DIGITAL_DATA.smiley(feedback),
        };
        setSatelliteDigitalData(OMNITURE_TRACKING_EVENTS.LINK_CLICK, omniDigitalData);
      } else {
        onFeedback({ isFeedbackGiven: false, error: res?.data?.message || SERVER_ERROR });
      }
    } catch (error) {
      onFeedback({ isFeedbackGiven: false, error: SERVER_ERROR });
    }
  };

  return (
    <span className={`${styles.emoji1} ${styles.emoji} ${feedbackGiven || isActive === feedback ? styles.active : ""}`}>
      <img src={src} alt={feedback} onClick={giveFeedback} />
    </span>
  );
});

SingleFeedback.displayName = "SingleFeedback";

const Feedback = ({ contentId, storage, url }) => {
  const [reactedContent, setReactedContent] = useLocalStorage(storage, []);
  const [isReacted, setIsReacted] = useState(null);

  useEffect(() => {
    const existingReaction = reactedContent.find((obj) => obj.id === contentId) || null;
    setIsReacted(existingReaction);
  }, [contentId, reactedContent]);

  const [feedbackGiven, setFeedbackGiven] = useMergeState({
    isFeedbackGiven: false,
    error: null,
    feedback: null,
  });

  const handleFeedback = useCallback((newValue) => {
    setFeedbackGiven(newValue);
  }, []);

  const saveFeedback = useCallback(
    (val) => {
      const updatedContent = [...reactedContent, { id: contentId, feedback: val }];
      setReactedContent(updatedContent);
    },
    [reactedContent, contentId, setReactedContent]
  );

  return (
    <>
      <span className={feedbackGiven.isFeedbackGiven || isReacted ? "pointer-none" : ""}>
        {["notSatisfied", "neutral", "satisfied"].map((feedback, index) => (
          <SingleFeedback
            key={feedback}
            contentId={contentId}
            src={`/images/emoji${index + 1}.png`}
            onFeedback={handleFeedback}
            feedback={feedback}
            onSave={saveFeedback}
            isActive={isReacted?.feedback}
            url={url}
          />
        ))}
      </span>
      {feedbackGiven.error && <span className="invalid-feedback">{feedbackGiven.error}</span>}
    </>
  );
};

Feedback.displayName = "Feedback";

export default Feedback;
